import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Divider, IconButton, List, ListItem,
  useTheme, useMediaQuery, TextField, CircularProgress, Alert, Chip, Tooltip, MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventIcon from "@mui/icons-material/Event";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/Email";
import { alpha } from "@mui/material/styles";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom";
import AlerteService from "../api/services/AlerteService";
import InfoIcon from "@mui/icons-material/Info";
const BRAND_GRADIENT = "linear-gradient(90deg, #2a5fa9 0%, #3a7fb5 20%, #4a9fc0 40%, #5ab8a0 60%, #70bd8d 80%, #8fc68b 100%)";

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

const resolveRole = (roleProp) => {
  if (roleProp === "admin" || roleProp === "gestionnaire") return roleProp;
  const stored =
    localStorage.getItem("user_role") ||
    localStorage.getItem("role") ||
    sessionStorage.getItem("user_role") ||
    sessionStorage.getItem("role") ||
    "gestionnaire";
  return stored === "admin" ? "admin" : "gestionnaire";
};

const resolveToken = (tokenProp) => {
  if (tokenProp) return tokenProp;
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("access_token") ||
    ""
  );
};

const getApiBase = (role) => {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const segment = role === "admin" ? "admin" : "gestionnaire";
  return `${base}/api/${segment}/actions`;
};

const buildHeaders = (token) => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});


export const ResiliationModal = ({ open, onClose, item, type, onSuccess, token: tokenProp, role: roleProp }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = resolveToken(tokenProp);
  const role = resolveRole(roleProp);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [letterContent, setLetterContent] = useState({
    recipientName: "",
    recipientContact: "",
    recipientAddress: "",
    recipientCP: "",
    recipientCity: "",
    recipientCountry: "",
    subject: "",
    body: "",
    sender: "",
    dateEnvoi: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    if (open && item && type === "contrat") {
      const userData = (() => {
        try { return JSON.parse(localStorage.getItem("user_data") || "{}"); } catch { return {}; }
      })();

      const dateSignatureFormatted = item.date_signature
        ? new Date(item.date_signature).toLocaleDateString("fr-FR") : "";
      const datePreavisFormatted = item.date_preavis
        ? new Date(item.date_preavis).toLocaleDateString("fr-FR") : "";
      const finContratFormatted = item.fin_contrat
        ? new Date(item.fin_contrat).toLocaleDateString("fr-FR") : "";

      setLetterContent({
        recipientName: item.nom_fournisseur || "",
        recipientContact: item.nom_contact || "",
        recipientAddress: item.adresse_fournisseur || "",
        recipientCP: item.cp_fournisseur || "",
        recipientCity: item.ville_fournisseur || "",
        recipientCountry: item.pays_fournisseur || "",
        subject: `Résiliation du contrat référencé : ${item.n_contact || ""}`,
        body: `Madame, Monsieur,\n\nPar la présente, nous vous informons de notre souhait de résilier le contrat cité en objet signé le ${dateSignatureFormatted}, conformément aux dispositions contractuelles.\n\nLe contrat prévoit un préavis de ${item.delai_preavis || ""} mois, débutant le ${datePreavisFormatted}, ce qui fixe la date effective de fin du contrat au ${finContratFormatted}.\n\nJe vous remercie de bien vouloir accuser réception de cette demande et de nous confirmer par écrit la prise en compte de cette résiliation à la date indiquée.\n\nJe reste à disposition pour organiser, le cas échéant, les modalités de clôture du contrat.\n\nVeuillez agréer, Madame, Monsieur, l'expression de mes salutations distinguées`,
        sender: userData.name || "",
        dateEnvoi: new Date().toISOString().slice(0, 10),
      });
      setError(null);
      setSuccess(null);
    } else if (open) {
      setError(null);
      setSuccess(null);
    }
  }, [open, item, type]);

  const getItemName = () => {
    switch (type) {
      case "marque": return item?.nom || "N/A";
      case "brevet": return item?.nom || "N/A";
      case "contrat": return item?.nom_fournisseur || "N/A";
      case "domaine": return item?.nom_domaine || "N/A";
      default: return "N/A";
    }
  };

  const getConfirmLabel = () => {
    if (type === "contrat") return "Résilier & télécharger PDF";
    if (type === "marque") return "Résilier la marque";
    if (type === "brevet") return "Résilier le brevet";
    if (type === "domaine") return "Résilier le domaine";
    return "Résilier";
  };

  const getConsequenceText = () => {
    if (type === "contrat") return " Vérifiez et téléchargez la lettre de résiliation au format PDF.";
    if (type === "marque") return "La marque passera à l'état « Non renouvelé ».";
    if (type === "brevet") return "Le brevet passera à l'état « Non renouvelé ».";
    if (type === "domaine") return "Le domaine passera à l'état « Non renouvelé ».";
    return "";
  };

  const generateResiliationPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const rightMargin = pageWidth - 20;
    const leftMargin = 20;
    const footerMargin = pageHeight - 15;

    const footerText = [
      "Groupe SOBER SAS – siège social",
      "391 rue de l'Avenir - Z.I. des Vernailles Ouest - 69830 SAINT GEORGES DE RENEINS - France",
      "T : 04.74.60.75.03 - F : 04.74.60.71.06 – M : commercial@sober.fr – S : www.sober.fr",
      "S.A.S. au capital de 1 100 000 € - 822 951 026 RCS Villefranche-Sur-Saône",
    ];

    // const addFooter = () => {
    //   doc.setDrawColor(135, 206, 250);
    //   doc.setLineWidth(0.5);
    //   doc.line(leftMargin, footerMargin - 5, pageWidth - 20, footerMargin - 5);
    //   doc.setFontSize(7);
    //   footerText.forEach((line, index) => {
    //     const lineWidth = doc.getTextWidth(line);
    //     const xPos = (pageWidth - lineWidth) / 2;
    //     if (index === 0) {
    //       doc.setFont("helvetica", "bold");
    //       doc.setTextColor(135, 206, 250);
    //     } else {
    //       doc.setFont("helvetica", "normal");
    //       doc.setTextColor(80, 80, 80);
    //     }
    //     doc.text(line, xPos, footerMargin + index * 3);
    //   });
    //   doc.setTextColor(0, 0, 0);
    //   doc.setFont("helvetica", "normal");
    //   doc.setFontSize(9);
    // };

    //  Logo 
    const getLogoPath = () => {
      const societeNom = (item?.societe_nom || "").toLowerCase().trim();
      if (societeNom.includes("soca medical")) return "/assets/SOCAMEDICAL.png";
      if (societeNom.includes("soca international")) return "/assets/SOCAINTERNATIONAL.png";
      if (societeNom.includes("mediprod")) return "/assets/MEDIPROD.png";
      if (societeNom.includes("medical digital")) return "/assets/MDA.png";
      if (societeNom.includes("groupe sober")) return "/assets/SOBER.png";
      if (societeNom.includes("FINAO Transition")) return "/assets/FINAO.png";
      return "/assets/SOBER.png";
    };

    try {
      const logoPath = getLogoPath();
      doc.addImage(logoPath, "PNG", 10, 10);
    } catch (e) {
      console.error("Logo:", e);
    }



    // Destinataire
    let yPos = 70;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(letterContent.recipientName, rightMargin, yPos, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    if (letterContent.recipientContact) { yPos += 5; doc.text(letterContent.recipientContact, rightMargin, yPos, { align: "right" }); }
    if (letterContent.recipientAddress) { yPos += 5; doc.text(letterContent.recipientAddress, rightMargin, yPos, { align: "right" }); }
    if (letterContent.recipientCP || letterContent.recipientCity) {
      yPos += 5;
      doc.text(`${letterContent.recipientCP || ""} ${letterContent.recipientCity || ""}`.trim(), rightMargin, yPos, { align: "right" });
    }
    if (letterContent.recipientCountry) { yPos += 5; doc.text(letterContent.recipientCountry, rightMargin, yPos, { align: "right" }); }

    // Objet
    yPos += 15;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const objetText = `Objet : ${letterContent.subject}`;
    doc.text(objetText, leftMargin, yPos);
    const objetWidth = doc.getTextWidth(objetText);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(leftMargin, yPos + 1, leftMargin + objetWidth, yPos + 1);

    // Date
    yPos += 8;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text(
      `St Georges de Reneins, le ${new Date(letterContent.dateEnvoi).toLocaleDateString("fr-FR")}`,
      leftMargin, yPos
    );

    // Corps
    yPos += 15;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const bodyText = doc.splitTextToSize(letterContent.body, 160);
    bodyText.forEach((line) => {
      if (yPos > pageHeight - 38) {
        // addFooter();
        doc.addPage(); yPos = 20;
      }
      doc.text(line, leftMargin, yPos);
      yPos += 5;
    });

    // Expéditeur
    yPos += 10;
    if (yPos > pageHeight - 50) {
      // addFooter();
      doc.addPage(); yPos = 20;
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(letterContent.sender, leftMargin, yPos);

    // Signature
    try {
      yPos += 8;
      if (yPos + 40 > footerMargin - 8) {
        // addFooter();
        doc.addPage(); yPos = 20;
      }
      doc.addImage("/assets/signature.png", "PNG", leftMargin, yPos, 40, 25);
      yPos += 28;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Eric LATTIER", leftMargin, yPos);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.text("Président", leftMargin, yPos + 5);
    } catch (e) { console.error("Signature:", e); }

    // addFooter();

    const pageCount = doc.internal.getNumberOfPages();
    if (pageCount > 1) {
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(128, 128, 128);
        doc.setFont("helvetica", "italic");
        doc.text(`Page ${i}/${pageCount}`, pageWidth - 25, 10, { align: "right" });
      }
    }

    const date = new Date().toISOString().split("T")[0];
    doc.save(`Lettre_resiliation_${item?.n_contact || "contrat"}_${date}.pdf`);
  };

  // ── Résilier via API puis générer PDF ─────────────────────────────────────
  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const apiUrl = `${getApiBase(role)}/resilier`;
      const body = {
        type,
        id: item.id,
        alerte_id: item.alerte_id || item.alerte?.id || null
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: buildHeaders(token),
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `Erreur ${response.status}`);

      // Suppression de la relation dans l'alerte
      await AlerteService.updateFromAlerte(token, role, {
        type: type,
        id: item.id,
        alerte_id: item.alerte_id || item.alerte?.id || null,
      });

      if (type === "contrat") {
        generateResiliationPDF();
        setSuccess("Contrat résilié avec succès. La lettre PDF a été téléchargée.");
      } else {
        setSuccess(`${getItemName()} résilié avec succès.`);
      }

      onSuccess?.(data.entity, type);

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Champs lettre ─────────────────────────────────────────────────────────
  const renderLetterFields = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
      <TextField
        label="Date de la lettre"
        type="date"
        value={letterContent.dateEnvoi}
        onChange={(e) => setLetterContent({ ...letterContent, dateEnvoi: e.target.value })}
        size="small" fullWidth InputLabelProps={{ shrink: true }}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
      <TextField
        label="Nom Fournisseur"
        value={letterContent.recipientName}
        onChange={(e) => setLetterContent({ ...letterContent, recipientName: e.target.value })}
        size="small" fullWidth
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
      <TextField
        label="Nom Contact"
        value={letterContent.recipientContact}
        onChange={(e) => setLetterContent({ ...letterContent, recipientContact: e.target.value })}
        size="small" fullWidth
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
      <TextField
        label="Adresse Fournisseur"
        value={letterContent.recipientAddress}
        onChange={(e) => setLetterContent({ ...letterContent, recipientAddress: e.target.value })}
        size="small" fullWidth
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <TextField
          label="Code Postal"
          value={letterContent.recipientCP}
          onChange={(e) => setLetterContent({ ...letterContent, recipientCP: e.target.value })}
          size="small" fullWidth
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
        <TextField
          label="Ville"
          value={letterContent.recipientCity}
          onChange={(e) => setLetterContent({ ...letterContent, recipientCity: e.target.value })}
          size="small" fullWidth
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </Box>
      <TextField
        label="Pays"
        value={letterContent.recipientCountry}
        onChange={(e) => setLetterContent({ ...letterContent, recipientCountry: e.target.value })}
        size="small" fullWidth
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
      <TextField
        label="Objet"
        value={letterContent.subject}
        onChange={(e) => setLetterContent({ ...letterContent, subject: e.target.value })}
        size="small" fullWidth
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
      <TextField
        label="Corps de la lettre"
        multiline rows={6}
        value={letterContent.body}
        onChange={(e) => setLetterContent({ ...letterContent, body: e.target.value })}
        size="small" fullWidth
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
      <TextField
        label="Expéditeur"
        value={letterContent.sender}
        onChange={(e) => setLetterContent({ ...letterContent, sender: e.target.value })}
        size="small" fullWidth
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "14px",
          background: isDark ? alpha("#1a1a1a", 0.97) : "#fff",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: BRAND_GRADIENT,
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CancelOutlinedIcon />
          <Typography fontWeight="bold" fontSize={{ xs: "1rem", sm: "1.1rem" }}>
            Résiliation — {getItemName()}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: "20px !important", pb: 2, px: { xs: 2, sm: 3 } }}>
        {success ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CheckCircleOutlineIcon sx={{ color: "#4caf50", fontSize: 52, mb: 1.5 }} />
            <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom>
              Contrat résilié
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {success}
            </Typography>
            <Box
              sx={{
                mt: 2, p: 2, borderRadius: "10px",
                background: alpha("#4caf50", 0.07),
                border: `1px solid ${alpha("#4caf50", 0.25)}`,
                display: "flex", alignItems: "center", gap: 1.5, justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                📄 La lettre de résiliation PDF a été téléchargée sur votre appareil.
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, borderRadius: "8px", fontSize: "0.85rem" }}>
              {getConsequenceText()}
            </Alert>
            {type === "contrat" ? renderLetterFields() : null}
          </>
        )}
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1 }}>
        {!success ? (
          <>
            <Button onClick={onClose} disabled={loading} sx={{ textTransform: "none" }}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CancelOutlinedIcon />}
              sx={{ textTransform: "none", background: BRAND_GRADIENT, "&:hover": { opacity: 0.9 }, borderRadius: "8px" }}
            >
              {loading ? "En cours…" : getConfirmLabel()}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={() => { onClose(); }}
            sx={{ textTransform: "none", background: BRAND_GRADIENT, "&:hover": { opacity: 0.9 }, borderRadius: "8px" }}
          >
            Terminer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};


export const RenouvellementModal = ({ open, onClose, item, type, onSuccess, token: tokenProp, role: roleProp }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const token = resolveToken(tokenProp);
  const role = resolveRole(roleProp);

  const [dateLabel, setDateLabel] = useState(null);
  const [nouvelleDate, setNouvelleDate] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statut, setStatut] = useState("");
  const [success, setSuccess] = useState(null);

  // Définition des statuts spécifiques pour chaque type
  const getStatutsByType = (type) => {
    switch (type) {
      case "marque":
        return [
          'Actif', 
          'Non actif', 
          'En cours', 
          'Renouvelé', 
          'Non Renouvelé', 
          'Problème', 
          'Protégé', 
          'Enregistré', 
          'Opposition', 
          'Opposable', 
          'Déposé', 
          'A déposer'
        ];
      case "brevet":
        return [
          'Actif', 
          'Déchu', 
          'Expiré', 
          'Renouvelé', 
          'Non Renouvelé', 
          'Déposé', 
          'A déposer', 
          'Opposable', 
          'Litige', 
          'Publier'
        ];
      case "contrat":
        return [
          'Actif', 
          'En cours', 
          'A renouveler', 
          'Expiré', 
          'Suspendu', 
          'Résilié'
        ];
      case "domaine":
        return [
          'Actif', 
          'Non actif', 
          'A fermer', 
          'En attente', 
          'Expiré', 
          'Renouvelé', 
          'Non renouvelé', 
          'Suspendu'
        ];
      default:
        return ['Actif', 'En cours', 'Renouvelé', 'En vigueur', 'Suspendu'];
    }
  };

  const getItemName = () => {
    switch (type) {
      case "marque": return item?.nom || "N/A";
      case "brevet": return item?.nom || "N/A";
      case "contrat": return item?.nom_fournisseur || "N/A";
      case "domaine": return item?.nom_domaine || "N/A";
      default: return "N/A";
    }
  };

  const getDateCurrentLabel = () => {
    switch (type) {
      case "contrat": return "Fin contrat actuelle";
      case "marque": return "Date de renouvellement actuelle";
      case "brevet": return "Date d'expiration actuelle";
      case "domaine": return "Date de renouvellement actuelle";
      default: return "Date actuelle";
    }
  };

  const getCurrentDate = () => {
    switch (type) {
      case "contrat": return formatDate(item?.fin_contrat);
      case "marque": return formatDate(item?.date_de_renouvellement);
      case "brevet": return formatDate(item?.date_expiration);
      case "domaine": return formatDate(item?.date_renouvellement);
      default: return null;
    }
  };

  const getInfoText = () => {
    switch (type) {
      case "contrat": return `Calculé : fin contrat + période de réengagement (${item?.periode_reengagement ?? 12} mois).`;
      case "marque": return "La date de renouvellement sera prolongée de 10 ans.";
      case "brevet": return "La date d'expiration sera prolongée de 20 ans.";
      case "domaine": return "La date de renouvellement sera prolongée d'1 an.";
      default: return "";
    }
  };

  // Récupérer le statut actuel de l'item
  const getCurrentStatut = () => {
    switch (type) {
      case "marque": return item?.etat_de_la_marque;
      case "brevet": return item?.etat;
      case "contrat": return item?.etat;
      case "domaine": return item?.statut;
      default: return "";
    }
  };

  useEffect(() => {
    if (!open || !item?.id) return;
    
    setLoadingPreview(true);
    setError(null);
    setDateLabel(null);
    setNouvelleDate("");
    
    // Récupérer le statut actuel de l'item
    const currentStatut = getCurrentStatut();
    const statutsOptions = getStatutsByType(type);
    
    console.log("Statut actuel de l'item:", currentStatut);
    console.log("Options disponibles:", statutsOptions);
    
    // Si le statut actuel existe dans la liste des options, le sélectionner
    if (currentStatut && statutsOptions.includes(currentStatut)) {
      setStatut(currentStatut);
      console.log("Statut actuel trouvé et sélectionné:", currentStatut);
    } 
    // Sinon, prendre le premier statut de la liste
    else if (statutsOptions.length > 0) {
      setStatut(statutsOptions[0]);
      console.log("Statut actuel non trouvé, sélection du premier:", statutsOptions[0]);
    }

    const previewUrl = `${getApiBase(role)}/renouveler/preview?type=${type}&id=${item.id}`;
    fetch(previewUrl, { headers: buildHeaders(token) })
      .then((r) => r.json())
      .then((data) => {
        setDateLabel(data.label ?? null);
        setNouvelleDate(data.date_calculee ?? "");
      })
      .catch(() => setError("Impossible de calculer la prochaine date."))
      .finally(() => setLoadingPreview(false));
  }, [open, item, type, token, role]);

  const handleConfirm = async () => {
    if (!nouvelleDate) {
      setError("Veuillez saisir une date de renouvellement.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = `${getApiBase(role)}/renouveler`;
      const body = {
        type,
        id: item.id,
        nouvelle_date: nouvelleDate,
        statut: statut,
        alerte_id: item.alerte_id || item.alerte?.id || null
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: buildHeaders(token),
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `Erreur ${response.status}`);

      await AlerteService.updateFromAlerte(token, role, {
        type: type,
        id: item.id,
        alerte_id: item.alerte_id || item.alerte?.id || null,
      });

      setSuccess("Renouvellement effectué avec succès.");
      onSuccess?.(data.entity, type);
      onClose();

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les options de statuts pour le type actuel
  const statutsOptions = getStatutsByType(type);
  const currentStatut = getCurrentStatut();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "14px",
          background: isDark ? alpha("#1a1a1a", 0.97) : "#fff",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: BRAND_GRADIENT,
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutorenewIcon />
          <Typography fontWeight="bold" fontSize={{ xs: "1rem", sm: "1.1rem" }}>
            Renouvellement — {getItemName()}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: "20px !important", pb: 2, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ p: 1.5, mb: 2, borderRadius: "10px", background: alpha("#2a5fa9", 0.06), border: `1px solid ${alpha("#2a5fa9", 0.18)}` }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {getDateCurrentLabel()} :
          </Typography>
          <Typography fontWeight="600" sx={{ background: BRAND_GRADIENT, backgroundClip: "text", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {getCurrentDate() || "Non renseignée"}
          </Typography>
        </Box>

        {/* Affichage du statut actuel */}
        {currentStatut && (
          <Alert 
            severity="info" 
            icon={<InfoIcon />} 
            sx={{ mb: 2, borderRadius: "8px", fontSize: "0.85rem" }}
          >
            <Typography variant="body2">
              <strong>Statut actuel :</strong> {currentStatut}
            </Typography>
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2.5, borderRadius: "8px", fontSize: "0.82rem" }}>
          {getInfoText()}
        </Alert>

        {loadingPreview ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Calcul de la prochaine date…</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {dateLabel && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, borderRadius: "10px", background: alpha("#4caf50", 0.07), border: `1px solid ${alpha("#4caf50", 0.3)}` }}>
                <EventIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Date calculée automatiquement</Typography>
                  <Typography fontWeight="600" color="#4caf50">{formatDate(dateLabel) || dateLabel}</Typography>
                </Box>
              </Box>
            )}
            <TextField
              label="Nouvelle date de renouvellement"
              type="date"
              value={nouvelleDate}
              onChange={(e) => setNouvelleDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Vous pouvez modifier la date proposée"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            
            {/* Champ statut avec le statut actuel pré-sélectionné */}
            <TextField
              select
              label="Nouveau statut"
              value={statut}
              onChange={(e) => setStatut(e.target.value)}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
              helperText={`Statut actuel: ${currentStatut || "Non défini"} - Choisissez le nouveau statut à appliquer`}
            >
              {statutsOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {option === currentStatut && (
                      <Chip 
                        label="Actuel" 
                        size="small" 
                        sx={{ 
                          height: 20, 
                          fontSize: "0.7rem",
                          bgcolor: alpha("#2a5fa9", 0.1),
                          color: "#2a5fa9"
                        }} 
                      />
                    )}
                    {option}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: "none" }}>Annuler</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || loadingPreview || !nouvelleDate}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
          sx={{ textTransform: "none", background: BRAND_GRADIENT, "&:hover": { opacity: 0.9 }, borderRadius: "8px" }}
        >
          {loading ? "Enregistrement…" : "Confirmer le renouvellement"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


const AlerteDetailsModal = ({
  open, onClose, alerteData, type, onItemUpdated,
  token: tokenProp,
  role: roleProp,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const token = resolveToken(tokenProp);
  const role = resolveRole(roleProp);

  const [resiliationModal, setResiliationModal] = useState({ open: false, item: null });
  const [renouvellementModal, setRenouvellementModal] = useState({ open: false, item: null });

  if (!alerteData) return null;

  const typeLabels = { marque: "Marques", brevet: "Brevets", domaine: "Domaines", contrat: "Contrats" };

  const getRouteByType = (type) => {
    switch (type) {
      case "marque":
        return "/marques";
      case "contrat":
        return "/contrats";
      case "brevet":
        return "/brevets";
      case "domaine":
        return "/NomDomaine";
      default:
        return "/";
    }
  };

  const getItemName = (item) => {
    switch (type) {
      case "marque": return item.nom || "N/A";
      case "brevet": return item.nom || "N/A";
      case "contrat": return item.nom_fournisseur || "N/A";
      case "domaine": return item.nom_domaine || "N/A";
      default: return "N/A";
    }
  };

  const getItemDetails = (item) => {
    const d = [];
    switch (type) {
      case "marque":
        if (item.etat_de_la_marque) d.push({ label: "État", value: item.etat_de_la_marque });
        if (item.date_de_renouvellement) d.push({ label: "Renouvellement", value: formatDate(item.date_de_renouvellement) });
        break;
      case "brevet":
        if (item.etat) d.push({ label: "État", value: item.etat });
        if (item.numero_brevet) d.push({ label: "Numéro", value: item.numero_brevet });
        if (item.date_expiration) d.push({ label: "Expiration", value: formatDate(item.date_expiration) });
        break;
      case "contrat":
        if (item.etat) d.push({ label: "État", value: item.etat });
        if (item.date_signature) d.push({ label: "Date signature", value: formatDate(item.date_signature) });
        if (item.fin_contrat) d.push({ label: "Fin contrat", value: formatDate(item.fin_contrat) });
        d.push({ label: "Réengagement", value: `${item.periode_reengagement || 12} mois` });
        if (item.delai_preavis) d.push({ label: "Délai préavis", value: `${item.delai_preavis} jours` });
        break;
      case "domaine":
        if (item.statut) d.push({ label: "État", value: item.statut });
        if (item.date_renouvellement) d.push({ label: "Renouvellement", value: formatDate(item.date_renouvellement) });
        if (item.periodicite) d.push({ label: "Périodicité", value: item.periodicite });
        break;
      default: break;
    }
    return d;
  };

  const getResiliationLabel = () => {
    if (type === "contrat") return "Envoyer email de résiliation";
    if (type === "domaine") return "Résilier le domaine";
    if (type === "marque") return "Résilier la marque";
    if (type === "brevet") return "Résilier le brevet";
    return "Résilier";
  };

  const getRenouvellementLabel = () => {
    if (type === "contrat") return "Renouveler le contrat";
    if (type === "domaine") return "Renouveler le domaine";
    if (type === "marque") return "Renouveler la marque";
    if (type === "brevet") return "Renouveler le brevet";
    return "Renouveler";
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "16px",
            background: isDark ? alpha("#000", 0.95) : alpha("#fff", 0.97),
            backdropFilter: "blur(10px)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: BRAND_GRADIENT,
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Alertes {typeLabels[type]} ({alerteData.items?.length || 0}) : {alerteData.nom_alerte}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#fff", "&:hover": { bgcolor: alpha("#fff", 0.2) } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, paddingTop: { xs: "24px !important", sm: "32px !important" } }}>
          {alerteData.items && alerteData.items.length > 0 ? (
            <List sx={{ width: "100%" }}>
              {alerteData.items.map((item, index) => (
                <Box key={item.id || index}>
                  <ListItem
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      bgcolor: alpha("#2a5fa9", 0.04),
                      borderRadius: "10px",
                      mb: 1.5,
                      p: { xs: 1.5, sm: 2 },
                      border: `1px solid ${alpha("#2a5fa9", 0.15)}`,
                      "&:hover": { bgcolor: alpha("#2a5fa9", 0.08) },
                      transition: "background 0.2s",
                    }}
                  >
                    <Box sx={{ width: "100%", mb: 1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                      <Typography component="span" sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, fontWeight: 500, color: isDark ? "#fff" : "#000" }}>
                        {type === "marque" && "Marque : "}
                        {type === "brevet" && "Brevet : "}
                        {type === "contrat" && "Contrat : "}
                        {type === "domaine" && "Domaine : "}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: "1.05rem", sm: "1rem" },
                          fontWeight: 700,
                          background: BRAND_GRADIENT,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          wordBreak: "break-word",
                          cursor: "pointer",
                          textDecoration: "underline",
                          "&:hover": {
                            opacity: 0.8
                          }
                        }}
                        onClick={() =>
                          navigate(getRouteByType(type), {
                            state: {
                              highlightItem:
                                type === "contrat"
                                  ? item.nom_fournisseur
                                  : type === "domaine"
                                    ? item.nom_domaine
                                    : type === "marque" || type === "brevet"
                                      ? item.nom
                                      : item.nom,

                              highlightId: item.marque_id || item.id,
                              highlightItem: getItemName(item),

                            },
                          })
                        }
                      >
                        {getItemName(item)}
                      </Typography>
                      {item.societe_nom && (
                        <Chip label={item.societe_nom} size="small" sx={{ height: 20, fontSize: "0.7rem", background: BRAND_GRADIENT, color: "#fff", fontWeight: 600, border: "none" }} />
                      )}
                    </Box>

                    {getItemDetails(item).length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%", mb: 1.5 }}>
                        {getItemDetails(item).map((detail, idx) => (
                          <Typography key={idx} variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                            <Typography component="span" sx={{ fontWeight: 500, color: isDark ? "#fff" : "#000" }}>{detail.label} : </Typography>
                            <Typography component="span" sx={{ color: isDark ? "#ccc" : "#444" }}>{detail.value}</Typography>
                          </Typography>
                        ))}
                      </Box>
                    )}

                    <Divider sx={{ width: "100%", mb: 1.5, borderColor: alpha("#2a5fa9", 0.15) }} />

                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
                      <Tooltip title={type === "contrat" ? "Résilier + envoyer email à la société" : type === "marque" ? "Résilier (La marque passera à l'état « Non renouvelé »)" : "Résilier"}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={type === "contrat" ? <EmailIcon /> : <CancelOutlinedIcon />}
                          onClick={() => setResiliationModal({ open: true, item })}
                          sx={{ textTransform: "none", borderColor: "#e57373", color: "#e57373", fontSize: { xs: "0.75rem", sm: "0.8rem" }, px: { xs: 1.5, sm: 2 }, borderRadius: "8px", "&:hover": { borderColor: "#c62828", bgcolor: alpha("#e57373", 0.08), color: "#c62828" } }}
                        >
                          {getResiliationLabel()}
                        </Button>
                      </Tooltip>

                      <Tooltip title={type === "marque" ? "+10 ans" : type === "brevet" ? "+20 ans" : type === "domaine" ? "+1 an" : "Renouveler"}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AutorenewIcon />}
                          onClick={() => setRenouvellementModal({ open: true, item })}
                          sx={{ textTransform: "none", borderColor: "#2a5fa9", color: "#2a5fa9", fontSize: { xs: "0.75rem", sm: "0.8rem" }, px: { xs: 1.5, sm: 2 }, borderRadius: "8px", "&:hover": { borderColor: "#3a7fb5", bgcolor: alpha("#2a5fa9", 0.08) } }}
                        >
                          {getRenouvellementLabel()}
                        </Button>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < alerteData.items.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
              <Typography variant="body2">Aucun élément trouvé pour cette alerte</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, borderTop: `1px solid ${alpha("#2a5fa9", 0.1)}` }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{ background: BRAND_GRADIENT, color: "#fff", textTransform: "none", px: 3, borderRadius: "8px", "&:hover": { opacity: 0.9 } }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <ResiliationModal
        open={resiliationModal.open}
        onClose={() => {
          setResiliationModal({ open: false, item: null });
          onItemUpdated?.(null, type);
        }}
        item={resiliationModal.item}
        type={type}
        onSuccess={(entity, t) => onItemUpdated?.(entity, t)}
        token={token}
        role={role}
      />

      <RenouvellementModal
        open={renouvellementModal.open}
        onClose={() => {
          setRenouvellementModal({ open: false, item: null });
          onItemUpdated?.(null, type);
        }}
        item={renouvellementModal.item}
        type={type}
        onSuccess={(entity, t) => onItemUpdated?.(entity, t)}
        token={token}
        role={role}
      />
    </>
  );
};

export default AlerteDetailsModal;